/**
 * The Item's one image (decision 0269): a picture of the product, stored no
 * larger than three inches a side at the resolution a document line prints
 * at. 🔨 Scaling only ever shrinks; a small image is stored as it came.
 */
export const ITEM_IMAGE_MAX_INCHES = 3;
export const ITEM_IMAGE_DPI = 300;
/** 900: three inches at 300 DPI, the cap on either side of a stored image. */
export const ITEM_IMAGE_MAX_PX = ITEM_IMAGE_MAX_INCHES * ITEM_IMAGE_DPI;
/** The seeded FileType every Item image takes; the modal never asks for one. */
export const ITEM_IMAGE_FILE_TYPE_NAME = 'Item Image';
export const ITEM_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;

export type ItemImageSizeKey = 'three' | 'two' | 'one';
/** The printed sizes the Item Image modal offers, largest first; each is a box the picture is scaled into. */
export const ITEM_IMAGE_SIZES: readonly { key: ItemImageSizeKey; inches: number }[] = [
  { key: 'three', inches: 3 },
  { key: 'two', inches: 2 },
  { key: 'one', inches: 1 },
];

export function isItemImageMime(mimeType: string): boolean {
  return (ITEM_IMAGE_MIME_TYPES as readonly string[]).includes(mimeType);
}

/** The pixel box a printed size occupies at the line's resolution, never wider than the stored cap. */
export function imageBoxFor(inches: number): number {
  return Math.min(ITEM_IMAGE_MAX_PX, Math.round(inches * ITEM_IMAGE_DPI));
}

/** The size an image takes inside a square box: scaled down to fit, never up, its proportions kept. */
export function fitWithin(width: number, height: number, maxPx: number): { width: number; height: number } {
  if (width <= 0 || height <= 0) return { width: 0, height: 0 };
  const longest = Math.max(width, height);
  if (longest <= maxPx) return { width, height };
  const ratio = maxPx / longest;
  return { width: Math.max(1, Math.round(width * ratio)), height: Math.max(1, Math.round(height * ratio)) };
}

/** The unit words a size is drawn with; English where a caller passes none, the mail pane's translated pair where it does. */
export interface ByteUnits {
  readonly b?: string;
  readonly kb: string;
  readonly mb: string;
}

const ENGLISH_BYTE_UNITS: ByteUnits = { b: 'B', kb: 'KB', mb: 'MB' };

/** The one file-size text (T661): bytes under 1 KB, whole KB, then MB to one decimal with a trailing .0 dropped: 512 B, 84 KB, 2.1 MB, 10 MB. */
export function byteText(bytes: number, units: ByteUnits = ENGLISH_BYTE_UNITS): string {
  if (bytes < 1024) return `${bytes} ${units.b ?? ENGLISH_BYTE_UNITS.b}`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} ${units.kb}`;
  return `${Number((bytes / (1024 * 1024)).toFixed(1))} ${units.mb}`;
}
